import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { ORG_LEVEL } from '../../types/enums/organization-level.enum';
import { TICKET_ACTION } from '../../types/enums/ticket-action.type';
import { TicketType } from '../../types/ticket/ticket.type';
import { TicketListItemType } from '../../types/ticket/TicketListItem.type';
import { UserType } from '../../types/user/user.type';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import {
  deleteAttachmentDetails,
  deleteTicketAttachmentDetails,
  getTicketDetails,
  getTicketGroups,
  getSupportTicketsStats,
  getTicketsList,
  getTicketUsersList,
  patchNewTicketDetails,
  patchTicketDetails,
  postAttachmentDetails,
  postCommentDetails,
  postTicketAttachmentDetails,
  postTicketAttachmentsDetails,
  postTicketComment,
  postTicketDetails,
} from './tickets.action';
import { TICKET_PRIORITY } from '../../types/enums/ticket-priority.type';
import { TICKET_STATUS } from '../../types/enums/ticket-status.enum';
import { TicketDepartment } from '../../types/enums/ticket-department.enum';
import { SupportTicketsStats } from '../../types/ticket/support-tickets-stats.type';

type TicketsRouteParams = {
  page: number;
  limit: number;
  order: string;
  q: string;

  priority: TICKET_PRIORITY | '';
  status: TICKET_STATUS | '';
  action: TICKET_ACTION | '';
  department: TicketDepartment | '';
  start_date: string;
  end_date: string;
  org_type: string;
};

export interface TicketsState {
  ticketsList: null | PaginationType<TicketListItemType>;
  ticketsListFetchStatus: FETCH_STATUS;
  ticketsRouteParams: TicketsRouteParams;
  ticketDetails: null | TicketType;
  ticketDetailsFetchStatus: FETCH_STATUS;
  ticketDetailsPostStatus: FETCH_STATUS;
  ticketDetailsPatchStatus: FETCH_STATUS;
  ticketUsersList: null | { items: Array<UserType> };
  ticketUsersListFetchStatus: FETCH_STATUS;
  ticketGroups: null | { items: Array<{ id: number; name: string; members: Array<UserType> }> };
  ticketGroupsFetchStatus: FETCH_STATUS;
  commentDetailsPostStatus: FETCH_STATUS;
  noteDetailsPostStatus: FETCH_STATUS;
  attachmentDetailsPostStatus: FETCH_STATUS;
  attachmentDetailsDeleteStatus: FETCH_STATUS;

  supportTicketsStats: SupportTicketsStats | null;
  supportTicketsStatsFetchStatus: FETCH_STATUS;
}

export const initialTicketsRouteParams: TicketsRouteParams = {
  page: 1,
  limit: 10,
  order: '-tickets.id',
  q: '',
  priority: '',
  status: '',
  action: '',
  department: '',
  start_date: '',
  end_date: '',
  org_type: '',
};

const INIT_STATE: TicketsState = {
  ticketsRouteParams: initialTicketsRouteParams,
  ticketsList: null,
  ticketsListFetchStatus: FETCH_STATUS.NULL,
  ticketDetails: null,
  ticketDetailsFetchStatus: FETCH_STATUS.NULL,
  ticketDetailsPostStatus: FETCH_STATUS.NULL,
  ticketDetailsPatchStatus: FETCH_STATUS.NULL,
  ticketUsersList: null,
  ticketUsersListFetchStatus: FETCH_STATUS.NULL,
  ticketGroups: null,
  ticketGroupsFetchStatus: FETCH_STATUS.NULL,
  commentDetailsPostStatus: FETCH_STATUS.NULL,
  noteDetailsPostStatus: FETCH_STATUS.NULL,
  attachmentDetailsPostStatus: FETCH_STATUS.NULL,
  attachmentDetailsDeleteStatus: FETCH_STATUS.NULL,
  supportTicketsStats: null,
  supportTicketsStatsFetchStatus: FETCH_STATUS.NULL,
};

const TICKET_CREATED_ATTACHMENT_FAILED_MESSAGE = 'was created successfully, but attachment upload failed';

const ticketsSlice = createSlice({
  name: 'tickets',
  initialState: INIT_STATE,
  reducers: {
    setTicketsList: (state, action) => {
      state.ticketsList = action.payload;
    },
    setTicketsListFetchStatus: (state, action) => {
      state.ticketsListFetchStatus = action.payload;
    },
    setTicketDetails: (state, action) => {
      state.ticketDetails = action.payload;
    },
    setTicketDetailsFetchStatus: (state, action) => {
      state.ticketDetailsFetchStatus = action.payload;
    },
    setTicketDetailsPostStatus: (state, action) => {
      state.ticketDetailsPostStatus = action.payload;
    },
    setTicketDetailsPatchStatus: (state, action) => {
      state.ticketDetailsPatchStatus = action.payload;
    },
    setTicketUsersList: (state, action) => {
      state.ticketUsersList = action.payload;
    },
    setTicketUsersListFetchStatus: (state, action) => {
      state.ticketUsersListFetchStatus = action.payload;
    },
    setTicketGroups: (state, action) => {
      state.ticketGroups = action.payload;
    },
    setTicketGroupsFetchStatus: (state, action) => {
      state.ticketGroupsFetchStatus = action.payload;
    },
    setCommentDetailsPostStatus: (state, action) => {
      state.commentDetailsPostStatus = action.payload;
    },
    setAttachmentDetailsPostStatus: (state, action) => {
      state.attachmentDetailsPostStatus = action.payload;
    },
    setAttachmentDetailsDeleteStatus: (state, action) => {
      state.attachmentDetailsDeleteStatus = action.payload;
    },
    setNoteDetailsPostStatus: (state, action) => {
      state.noteDetailsPostStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: TicketsRouteParams }>;
      if (payload.reducer === 'tickets') {
        state.ticketsRouteParams = { ...state.ticketsRouteParams, ...payload.data };
      }
    });

    // CREATE TICKETS DETAILS
    builder.addCase(postTicketDetails.pending, state => {
      state.ticketDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postTicketDetails.rejected, (state, action) => {
      const errorMessage = (action.payload as string) || '';
      const isTicketCreatedWithAttachmentFailure = errorMessage.includes(TICKET_CREATED_ATTACHMENT_FAILED_MESSAGE);

      state.ticketDetailsPostStatus = isTicketCreatedWithAttachmentFailure
        ? FETCH_STATUS.FULFILLED
        : FETCH_STATUS.REJECTED;
      setTimeout(() => {
        if (isTicketCreatedWithAttachmentFailure) {
          snackbarUtils.warning(errorMessage);
          return;
        }

        snackbarUtils.error(errorMessage);
      });
    });
    builder.addCase(postTicketDetails.fulfilled, state => {
      state.ticketDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Ticket has been opened!');
      });
    });

    // GET TICKETS LIST
    builder.addCase(getTicketsList.pending, state => {
      state.ticketsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getTicketsList.rejected, (state, action) => {
      if (action.payload) {
        state.ticketsListFetchStatus = FETCH_STATUS.REJECTED;
        // state.ticketsList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getTicketsList.fulfilled, (state, action) => {
      state.ticketsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.ticketsList = action.payload;
    });

    // GET TICKETS DETAILS
    builder.addCase(getTicketDetails.pending, state => {
      state.ticketDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getTicketDetails.rejected, (state, action) => {
      state.ticketDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.ticketDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getTicketDetails.fulfilled, (state, action) => {
      state.ticketDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.ticketDetails = action.payload;
    });

    // PATCH TICKETS DETAILS
    builder.addCase(patchTicketDetails.pending, state => {
      state.ticketDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchTicketDetails.rejected, (state, action) => {
      state.ticketDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchTicketDetails.fulfilled, state => {
      state.ticketDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Tickets updated successfully');
      });
    });

    builder.addCase(patchNewTicketDetails.pending, state => {
      state.ticketDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchNewTicketDetails.rejected, (state, action) => {
      state.ticketDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchNewTicketDetails.fulfilled, state => {
      state.ticketDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Tickets updated successfully');
      });
    });

    // GET TICKET USERS LIST
    builder.addCase(getTicketUsersList.pending, state => {
      state.ticketUsersListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getTicketUsersList.rejected, (state, action) => {
      state.ticketUsersListFetchStatus = FETCH_STATUS.REJECTED;
      state.ticketUsersList = null;
      snackbarUtils.error(action.payload);
    });
    builder.addCase(getTicketUsersList.fulfilled, (state, action) => {
      state.ticketUsersListFetchStatus = FETCH_STATUS.FULFILLED;
      state.ticketUsersList = action.payload;
    });

    // GET TICKET GROUPS
    builder.addCase(getTicketGroups.pending, state => {
      state.ticketGroupsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getTicketGroups.rejected, (state, action) => {
      state.ticketGroupsFetchStatus = FETCH_STATUS.REJECTED;
      state.ticketGroups = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getTicketGroups.fulfilled, (state, action) => {
      state.ticketGroupsFetchStatus = FETCH_STATUS.FULFILLED;
      state.ticketGroups = action.payload;
    });

    // POST COMMENT
    builder.addCase(postCommentDetails.pending, state => {
      state.commentDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postCommentDetails.rejected, (state, action) => {
      state.commentDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postCommentDetails.fulfilled, state => {
      state.commentDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Comment has been added!'));
    });

    // POST ATTACHMENT
    builder.addCase(postAttachmentDetails.pending, state => {
      state.attachmentDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postAttachmentDetails.rejected, (state, action) => {
      state.attachmentDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postAttachmentDetails.fulfilled, state => {
      state.attachmentDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Attachment was added successfully!'));
    });

    builder.addCase(postTicketAttachmentDetails.pending, state => {
      state.attachmentDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postTicketAttachmentDetails.rejected, (state, action) => {
      state.attachmentDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postTicketAttachmentDetails.fulfilled, (state, action) => {
      state.attachmentDetailsPostStatus = FETCH_STATUS.FULFILLED;
      if (state.ticketDetails) {
        const tmp = { ...state.ticketDetails };
        state.ticketDetails = {
          ...tmp,
          attachments: [
            {
              ...action.payload,
              createdAt: new Date(+Date.now() - 1000).toISOString(),
            },
            ...tmp.attachments,
          ],
        };
      }
      setTimeout(() => snackbarUtils.success('Attachment was added successfully!'));
    });

    builder.addCase(postTicketAttachmentsDetails.pending, state => {
      state.attachmentDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postTicketAttachmentsDetails.rejected, (state, action) => {
      state.attachmentDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postTicketAttachmentsDetails.fulfilled, (state, action) => {
      state.attachmentDetailsPostStatus = FETCH_STATUS.FULFILLED;
      if (state.ticketDetails && action.payload) {
        const tmp = { ...state.ticketDetails };
        const newAttachments = Array.isArray(action.payload) ? action.payload : [action.payload];
        state.ticketDetails = {
          ...tmp,
          attachments: [
            ...newAttachments.map(att => ({
              ...att,
              createdAt: new Date(+Date.now() - 1000).toISOString(),
            })),
            ...tmp.attachments,
          ],
        };
      }
      setTimeout(() => snackbarUtils.success('Attachments were added successfully!'));
    });

    // DELETE ATTACHMENT
    builder.addCase(deleteAttachmentDetails.pending, state => {
      state.attachmentDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteAttachmentDetails.rejected, (state, action) => {
      state.attachmentDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteAttachmentDetails.fulfilled, state => {
      state.attachmentDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Attachment has been removed!'));
    });

    // POST COMMENT ORDER DETAILS
    builder.addCase(postTicketComment.pending, state => {
      state.commentDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postTicketComment.rejected, (state, action) => {
      state.commentDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postTicketComment.fulfilled, (state, action) => {
      state.commentDetailsPostStatus = FETCH_STATUS.FULFILLED;
      if (state.ticketDetails) {
        const tmp = { ...state.ticketDetails };
        if (!action.payload.isInternal) {
          if (action.meta.arg.data.orgLevel === ORG_LEVEL.TELCLOUD) {
            if (action.meta.arg.data.markAsWaitingForResponse) tmp.action = TICKET_ACTION.WAITING_FOR_RESPONSE;
            tmp.upperResponseDate = new Date(+Date.now() - 1000).toISOString();
          } else {
            tmp.action = TICKET_ACTION.NEEDS_ATTENTION;
          }
        }
        state.ticketDetails = {
          ...tmp,
          comments: [{ ...action.payload, createdAt: new Date(+Date.now() - 1000).toISOString() }, ...tmp.comments],
        };
      }
      setTimeout(() => {
        snackbarUtils.success('Comment created successfully!');
      });
    });

    builder.addCase(deleteTicketAttachmentDetails.pending, state => {
      state.attachmentDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteTicketAttachmentDetails.rejected, (state, action) => {
      state.attachmentDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteTicketAttachmentDetails.fulfilled, state => {
      state.attachmentDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => snackbarUtils.success('Attachment has been removed!'));
    });

    builder
      .addCase(getSupportTicketsStats.pending, state => {
        state.supportTicketsStatsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getSupportTicketsStats.rejected, (state, action) => {
        state.supportTicketsStatsFetchStatus = FETCH_STATUS.REJECTED;
        state.supportTicketsStats = null;
        snackbarUtils.error(action.payload);
      })
      .addCase(getSupportTicketsStats.fulfilled, (state, action) => {
        state.supportTicketsStatsFetchStatus = FETCH_STATUS.FULFILLED;
        state.supportTicketsStats = action.payload;
      });
  },
});

export const {
  setTicketsListFetchStatus,
  setTicketDetailsFetchStatus,
  setTicketDetailsPatchStatus,
  setTicketDetailsPostStatus,
  setTicketsList,
  setTicketDetails,
  setTicketUsersListFetchStatus,
  setTicketUsersList,
  setCommentDetailsPostStatus,
  setAttachmentDetailsDeleteStatus,
  setAttachmentDetailsPostStatus,
  setNoteDetailsPostStatus,
} = ticketsSlice.actions;

export default ticketsSlice.reducer;
